import {
	fontUtils,
	textUtils,
} from 'utils';
import {
	SOFT_HYPHEN,
	type WidthScaleKey,
} from 'utils/text';
import type {
	CSSProperties,
	IntrinsicElementAttributes,
	SVGAttributes,
} from 'vue';

export interface DomElement<
	Tag extends keyof IntrinsicElementAttributes,
	Namespace = Tag extends keyof SVGElementTagNameMap ? 'http://www.w3.org/2000/svg' : never,
> {
	tag: Tag;
	props: Partial<IntrinsicElementAttributes[Tag]>;
	namespace?: Namespace;
	children?: Array<DomElement<keyof IntrinsicElementAttributes> | string>;
}

export interface FullyVisibleResult {
	isBottomVisible: boolean;
	isFullyVisible: boolean;
	isLeftVisible: boolean;
	isRightVisible: boolean;
	isTopVisible: boolean;
}

export interface InsertSoftHyphensOptions {
	fontFamily: string;
	fontSize: number;
	maxWidth: number;
	text: string;
}

export interface TextareaToDomElementTextOptions {
	fill?: string;
	fontFamily: string;
	fontSize: number;
	lineHeight?: number;
	maxWidth: number;
	text: string;
	textAlign?: CSSProperties['textAlign'];
}

const softHyphensTextWidthCache = new Map<WidthScaleKey, Map<string, number>>();

/**
 * Produces a deep clone of a {@link DomElement} tree so it can be safely reused without side effects.
 *
 * @param element Root {@link DomElement} to clone.
 * @returns Deep-cloned {@link DomElement} instance.
 */
export function cloneDomElement(
	element: DomElement<keyof IntrinsicElementAttributes>,
): DomElement<keyof IntrinsicElementAttributes> {
	const clonedChildren = element.children?.map((child) => (
		typeof child === 'string'
			? child
			: cloneDomElement(child)
	));

	return {
		children: clonedChildren,
		namespace: element.namespace,
		props: (
			element.props
			?? {}
		),
		tag: element.tag,
	};
}

/**
 * Transforms the provided {@link SVGElement} tree into a {@link DomElement}
 * structure so it can be converted into a Vue {@link VNode}.
 *
 * @param element Root {@link SVGElement} of the parsed SVG document.
 * @returns {@link DomElement} describing the same SVG tree as {@link element}.
 */
export function convertSVGElementToDomElement<T extends SVGElement>(element: T): DomElement<keyof IntrinsicElementAttributes> {
	const tagName = element.tagName as keyof IntrinsicElementAttributes;
	const elementProps: SVGAttributes = {};

	type SvgAttributeEntry = {
		[AttributeName in keyof SVGAttributes]: {
			name: AttributeName;
			value: SVGAttributes[AttributeName];
		};
	}[keyof SVGAttributes];

	const attributes = Array.from(element.attributes) as SvgAttributeEntry[];

	for (const attribute of attributes) {
		if (!attribute) {
			continue;
		}

		elementProps[attribute.name] = attribute.value;
	}

	const children: Array<DomElement<keyof IntrinsicElementAttributes> | string> = [];
	const childNodes = Array.from(element.childNodes);

	for (const child of childNodes) {
		if (child.nodeType === Node.ELEMENT_NODE) {
			children.push(
				convertSVGElementToDomElement(child as SVGElement),
			);
		} else if (
			child.nodeType === Node.TEXT_NODE
			|| child.nodeType === Node.CDATA_SECTION_NODE
		) {
			const textContent = (
				child.textContent
				?? ''
			);

			if (textContent.length) {
				children.push(textContent);
			}
		}
	}

	return createDomElement(
		tagName,
		elementProps,
		children,
		'http://www.w3.org/2000/svg',
	);
}

export function createCanvasContext(font: string): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
	const canvasElement = (
		typeof OffscreenCanvas !== 'undefined'
			? new OffscreenCanvas(
				1,
				1,
			)
			: document.createElement('canvas')
	);
	const canvasContext = canvasElement.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;

	if (!canvasContext) {
		throw new Error('2D context not available');
	}

	canvasContext.font = font;

	return canvasContext;
}

export function createDomElement<
	Tag extends keyof IntrinsicElementAttributes,
	Namespace extends Tag extends keyof SVGElementTagNameMap ? 'http://www.w3.org/2000/svg' : never,
>(
	tag: Tag,
	props?: Partial<IntrinsicElementAttributes[Tag]>,
	children?: DomElement<Tag>['children'] | string,
	namespace?: Namespace,
): DomElement<Tag> {
	return {
		tag,
		namespace,
		props: (
			props
			?? {}
		),
		children: (
			typeof children === 'string'
				? [children]
				: children
		),
	};
}

export function domElementToHTMLElement(domElement: DomElement<keyof IntrinsicElementAttributes>): HTMLElement | SVGElement {
	let element: HTMLElement | SVGElement;

	if (!domElement.namespace) {
		element = document.createElement(domElement.tag);
	} else {
		element = document.createElementNS(
			domElement.namespace as 'http://www.w3.org/2000/svg',
			domElement.tag,
		) as SVGElement;
	}

	for (const [
		key,
		value,
	] of Object.entries(domElement.props)) {
		element.setAttribute(
			key,
			String(value),
		);
	}

	for (const child of domElement.children || []) {
		element.appendChild(
			typeof child === 'string'
				? document.createTextNode(child)
				: domElementToHTMLElement({
					...child,
					namespace: domElement.namespace,
				}),
		);
	}

	return element;
}

/**
 * Recursively searches for a DOM element that matches the provided predicate.
 *
 * @param element The DOM element to search within.
 * @param predicate A function that defines the search criteria.
 * @returns The found DOM element or null if not found.
 */
/**
 * Locates the first descendant {@link DomElement} whose tag matches the supplied `tag`.
 *
 * @param element Root {@link DomElement} to scan.
 * @param tag {@link keyof IntrinsicElementAttributes} value to match.
 * @returns Matching {@link DomElement} when discovered, otherwise `null`.
 */
export function findDomElement<Tag extends keyof IntrinsicElementAttributes>(
	element: DomElement<keyof IntrinsicElementAttributes>,
	tag: Tag,
): DomElement<Tag> | null;
/**
 * Locates the first descendant {@link DomElement} that satisfies the provided `predicate`.
 *
 * @param element Root {@link DomElement} to scan.
 * @param predicate Type guard narrowing a {@link DomElement} to the desired tag-specific element.
 * @returns Matching {@link DomElement} when discovered, otherwise `null`.
 */
export function findDomElement<Tag extends keyof IntrinsicElementAttributes>(
	element: DomElement<keyof IntrinsicElementAttributes>,
	predicate: (element: DomElement<keyof IntrinsicElementAttributes>) => element is DomElement<Tag>,
): DomElement<Tag> | null;
/**
 * Locates the first descendant {@link DomElement} that matches either a tag name or the supplied predicate.
 *
 * @param element Root {@link DomElement} to scan.
 * @param tagOrPredicate Tag name or predicate describing the desired {@link DomElement}.
 * @returns Matching {@link DomElement} when discovered, otherwise `null`.
 */
export function findDomElement<Tag extends keyof IntrinsicElementAttributes>(
	element: DomElement<keyof IntrinsicElementAttributes>,
	tagOrPredicate: Tag | ((element: DomElement<keyof IntrinsicElementAttributes>) => element is DomElement<Tag>),
): DomElement<Tag> | null {
	const predicate: (element: DomElement<keyof IntrinsicElementAttributes>) => element is DomElement<Tag> = (
		typeof tagOrPredicate === 'function'
			? tagOrPredicate
			: (candidate): candidate is DomElement<Tag> => candidate.tag === tagOrPredicate
	);

	if (predicate(element)) {
		return element;
	}

	if (element.children) {
		for (const child of element.children) {
			if (typeof child === 'string') {
				continue;
			}

			const found = findDomElement(
				child,
				predicate,
			);

			if (found) {
				return found;
			}
		}
	}

	return null;
}

/**
 * Compute the center of a DOMRect.
 * @param rect The DOMRect to compute the center of.
 * @returns A tuple containing the x and y coordinates of the center of the DOMRect.
 */
export function getDOMRectCenter(rect: DOMRect): [x: number, y: number] {
	return [
		rect.left + (rect.width / 2),
		rect.top + (rect.height / 2),
	];
}

/**
 * Inject **soft-hyphen characters ({@link SOFT_HYPHEN})** at every position where the
 * browser would break a long word when the text is wrapped at `maxWidth`.
 *
 * The routine walks the input one code-point at a time while mirroring the
 * browser's line-wrapping rules:
 *
 * 1. **Natural breaks**
 *    When the next glyph still fits, copy it verbatim.
 * 2. **Whitespace opportunity**
 *    If the overflow happens *after* a space in the current line, we drop
 *    everything *after* that space to the next visual line (no hyphen).
 * 3. **In-word overflow**
 *    When the line already contains only a single word, we:
 *    * pull the **last glyph** off that line,
 *    * replace it with a soft-hyphen,
 *    * start the next line with the pulled glyph + the overflow glyph.
 *    This keeps the hyphen on the first line and avoids double-wide lines.
 *
 * New-line characters reset the width budget so explicit hard returns are
 * preserved unchanged.
 *
 * @param options.text        Original textarea value.
 * @param options.maxWidth    Line width limit in CSS pixels.
 * @param options.fontFamily  Font used in the textarea/SVG.
 * @param options.fontSize    Font size (px) used for precise measurement.
 *
 * @returns The same text but **augmented with {@link SOFT_HYPHEN}** so that subsequent
 *          layout engines (SVG/PDF) can break the word exactly like the
 *          browser does.
 */
export function insertSoftHyphens(options: InsertSoftHyphensOptions): string {
	const {
		text,
		maxWidth,
		fontFamily,
		fontSize,
	} = options;
	const measure = textUtils.measure(
		fontFamily,
		fontSize,
		softHyphensTextWidthCache,
		/**
		 * Increased cache limit to 20000 since the soft hyphen insertion may
		 * involve measuring per word and per character, leading to a larger
		 * variety of strings being measured.
		 */
		20000,
	);
	/**
	 * Initialize variables:
	 * - `result` to build the final text with soft-hyphens.
	 * - `currentLine` to track the current line being built.
	 */
	let result = '';
	let currentLine = '';

	/**
	 * 1. We split with a *capturing* regex so that `\n`/`\r\n` separators are
	 * returned as tokens as well (and preserved exactly).
	 */
	const paragraphs = text.split(/(\r?\n)/);

	for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex += 1) {
		const paragraph = paragraphs[paragraphIndex];

		/**
		 * 1.a Handle newline characters by resetting the current line.
		 */
		if (
			paragraph === '\n'
			|| paragraph === '\r\n'
		) {
			result += paragraph;
			currentLine = '';
			continue;
		}

		/**
		 * 1.b Skip empty paragraphs (can happen with consecutive newlines).
		 */
		if (!paragraph) {
			continue;
		}

		/**
		 * 1.c Check entire paragraph fit:
		 * 		- If yes, copy it verbatim
		 * 		- If no, fall through to phase 2
		 */
		if (measure(paragraph) <= maxWidth) {
			result += paragraph;
			currentLine = paragraph;
			continue;
		}

		/**
		 * 2. **Pre-scan by words** (tokens separated by whitespace)
		 *    This loop copies entire words as long as they fit.
		 *    It stops at the first overflowing word, which is then
		 *    processed by the original character-wise logic in phase 3.
		 */
		const tokens = paragraph.split(/(\s+)/);
		let consumedLength = 0;
		let overflowFound = false;

		for (let t = 0; t < tokens.length; t += 1) {
			const token = tokens[t];

			if (!token) {
				continue;
			}

			const tentative = currentLine + token;

			if (measure(tentative) <= maxWidth) {
				/**
				 * 2.a Token fits - copy it verbatim and continue to the next token.
				 */
				currentLine = tentative;
				result += token;
				consumedLength += token.length;
				continue;
			}

			/**
			 * 2.b First overflowing token encountered - stop the pre-scan here.
			 * The remainder of the paragraph (starting at this token) will
			 * be processed with the original character-wise logic.
			 */
			overflowFound = true;
			break;
		}

		/**
		 * 3. **Character-wise processing of the remainder** (after the pre-scan)
		 *    This loop processes the remainder of the paragraph one character
		 *    at a time, applying the line-breaking rules described above.
		 */
		if (overflowFound) {
			const remainder = paragraph.slice(consumedLength);

			for (const character of remainder) {
				/**
				 * Handle new-line characters by resetting the current line.
				 */
				if (
					character === '\n'
					|| character === '\r'
				) {
					result += character;
					currentLine = '';
					continue;
				}

				const tentative = currentLine + character;

				/**
				 * Check if the tentative line fits within the maxWidth.
				 * If it does, update the current line and result.
				 */
				if (measure(tentative) <= maxWidth) {
					currentLine = tentative;
					result += character;
					continue;
				}

				/**
				 * Find the last space in the current line to determine where to break.
				 */
				const lastSpaceIdx = currentLine.search(/\s(?=[^\s]*$)/);

				/**
				 * If a space is found, move the text after the last space to the next line.
				 */
				if (lastSpaceIdx !== -1) {
					const trailingWord = currentLine.slice(lastSpaceIdx + 1);
					currentLine = trailingWord + character;
					result += character;
					continue;
				}

				/**
				 * Handle in-word overflow:
				 * - Replace the last glyph with a soft-hyphen.
				 * - Start the next line with the last glyph and the current character.
				 */
				if (currentLine.length > 0) {
					const lastGlyph = currentLine.slice(-1);
					currentLine = currentLine.slice(
						0,
						-1,
					);
					result = result.slice(
						0,
						-1,
					) + SOFT_HYPHEN;
					const newSegment = lastGlyph + character;
					currentLine = newSegment;
					result += newSegment;
					continue;
				}

				/**
				 * If the current line is empty and the character doesn't fit,
				 * insert a soft-hyphen before the character.
				 */
				result += SOFT_HYPHEN + character;
				currentLine = character;
			}
		}
	}

	return result;
}

export function isFullyVisible(element: HTMLElement): FullyVisibleResult {
	const result: FullyVisibleResult = {
		isBottomVisible: true,
		isFullyVisible: true,
		isLeftVisible: true,
		isRightVisible: true,
		isTopVisible: true,
	};

	if (!element) {
		return {
			isBottomVisible: false,
			isFullyVisible: false,
			isLeftVisible: false,
			isRightVisible: false,
			isTopVisible: false,
		};
	}

	const elementRect = element.getBoundingClientRect();
	let currentElement = element;

	while (
		currentElement
		&& currentElement.parentElement
	) {
		const { parentElement } = currentElement;
		const parentRect = parentElement.getBoundingClientRect();

		const isScrollNotMatching = (
			element.scrollWidth > parentElement.scrollWidth
			|| element.scrollHeight > parentElement.scrollHeight
		);
		const isTopNotVisible = elementRect.top < parentRect.top;
		const isLeftNotVisible = elementRect.left < parentRect.left;
		const isRightNotVisible = Math.floor(parentRect.left + parentElement.scrollWidth) < Math.floor(elementRect.left + element.scrollWidth);
		const isBottomNotVisible = Math.floor(parentRect.top + parentElement.scrollHeight) < Math.floor(elementRect.top + element.scrollHeight);

		if (
			isScrollNotMatching
			|| isTopNotVisible
			|| isLeftNotVisible
			|| isRightNotVisible
			|| isBottomNotVisible
		) {
			result.isFullyVisible = false;
		}

		if (isTopNotVisible) {
			result.isTopVisible = false;
		}
		if (isLeftNotVisible) {
			result.isLeftVisible = false;
		}
		if (isRightNotVisible) {
			result.isRightVisible = false;
		}
		if (isBottomNotVisible) {
			result.isBottomVisible = false;
		}

		currentElement = currentElement.parentElement;
	}

	return result;
}

export function isOrHasParent(
	element: HTMLElement,
	parent: HTMLElement,
): boolean {
	if (element === parent) {
		return true;
	}

	if (!element.parentElement) {
		return false;
	}

	return isOrHasParent(
		element.parentElement,
		parent,
	);
}

export function isRectScrollable(
	element: HTMLElement,
	parentToStop?: HTMLElement,
): boolean {
	const isElementScrollable = !(
		element.scrollWidth <= element.clientWidth
		&& element.scrollHeight <= element.clientHeight
	);

	if (
		isElementScrollable
		|| !element.parentElement
		|| element.parentElement.id === 'webapp'
		|| parentToStop?.isSameNode(element.parentElement)
	) {
		return isElementScrollable;
	}

	return isRectScrollable(
		element.parentElement,
		parentToStop,
	);
}

export function onElementChildrenChange(
	element: Element,
	callback: () => void,
): () => void {
	if (!element) {
		return () => undefined;
	}

	const observersDisconnects: (() => void)[] = [];
	const onImageLoad = (
		target: HTMLElement,
		onLoad: () => void,
	) => {
		const styles = getComputedStyle(target);

		if (target instanceof HTMLImageElement) {
			if (
				target.complete
				&& target.naturalWidth !== 0
			) {
				onLoad();
			} else {
				target.addEventListener(
					'load',
					() => onLoad(),
				);
			}
		} else if (styles.backgroundImage !== 'none') {
			const image = new Image();
			image.addEventListener(
				'load',
				() => onLoad(),
			);

			if (styles.backgroundImage.startsWith('url(')) {
				image.src = styles.backgroundImage
					.slice(
						4,
						-1,
					)
					.replace(
						/("|')/g,
						'',
					);
			} else {
				image.src = styles.backgroundImage;
			}
		}
	};
	const checkForImagesLoad = (
		target: HTMLElement,
		onLoad: () => void,
	) => {
		onImageLoad(
			target,
			onLoad,
		);

		if (target.children.length > 0) {
			Array.prototype.forEach.call(
				target.children,
				(child: HTMLElement) => {
					checkForImagesLoad(
						child as HTMLElement,
						onLoad,
					);
				},
			);
		}
	};
	const mutationObserver = new MutationObserver((mutationsList) => {
		if (
			mutationsList.every((mutation) => (
				mutation.target instanceof HTMLInputElement
				|| mutation.attributeName === 'class'
				|| mutation.attributeName === 'style'
			))
		) {
			return;
		}

		callback();

		for (const mutation of mutationsList) {
			if (mutation.type === 'childList') {
				const target = mutation.target as HTMLElement;

				Array.prototype.forEach.call(
					target.children,
					(child: HTMLElement) => {
						onImageLoad(
							child,
							callback,
						);

						if (child.children.length > 0) {
							observersDisconnects.push(
								onElementChildrenChange(
									child,
									callback,
								),
							);
						}
					},
				);
			} else if (mutation.type === 'attributes') {
				if (mutation.target instanceof HTMLElement) {
					onImageLoad(
						mutation.target,
						callback,
					);
				}
			}
		}
	});
	mutationObserver.observe(
		element,
		{
			childList: true,
			subtree: true,
			attributes: true,
			characterData: true,
		},
	);
	const resizeObserver = new ResizeObserver(() => callback());
	resizeObserver.observe(element);
	observersDisconnects.push(() => resizeObserver.disconnect());
	observersDisconnects.push(() => mutationObserver.disconnect());
	checkForImagesLoad(
		element as HTMLElement,
		callback,
	);

	return () => {
		for (const observerDisconnect of observersDisconnects) {
			observerDisconnect();
		}
	};
}

export function stripHtmlEntities(text: string): string {
	return text
		.replace(
			/&nbsp;/g,
			' ',
		)
		.replace(
			/&amp;/g,
			'&',
		)
		.replace(
			/&lt;/g,
			'<',
		)
		.replace(
			/&gt;/g,
			'>',
		)
		.replace(
			/&quot;/g,
			'"',
		)
		.replace(
			/&apos;/g,
			"'",
		);
}

/**
 * Converts text from a textarea into an SVG `<text>` DOM element, applying
 * line wrapping, text alignment, and font styling. The text is wrapped to fit
 * within the specified `maxWidth`, and each line is positioned correctly
 * within the SVG using `<tspan>` elements.
 *
 * @param options - Configuration options for text conversion.
 * @param options.fill - The color of the text (default: '#000').
 * @param options.fontFamily - The font family to use for the text.
 * @param options.fontSize - The font size in pixels.
 * @param options.lineHeight - The line height multiplier (default: {@link fontUtils.DEFAULT_LINEHEIGHT}).
 * @param options.maxWidth - The maximum width in pixels for each line.
 * @param options.text - The text content to convert.
 * @param options.textAlign - The text alignment ('left', 'center', 'right'; default: 'left').
 * @returns An SVG `<text>` DOM element containing the formatted text.
 */
export function textareaToDomElementText(options: TextareaToDomElementTextOptions): DomElement<'text'> {
	const {
		fill = '#000',
		fontFamily,
		fontSize,
		lineHeight = fontUtils.DEFAULT_LINEHEIGHT,
		maxWidth,
		text,
		textAlign = 'left',
	} = options;
	const measure = textUtils.measure(
		fontFamily,
		fontSize,
	);
	/**
	 * Wrap the text into lines that fit within the maxWidth
	 */
	const { lines } = textUtils.wrapLines(
		text,
		maxWidth,
		measure,
	);
	/**
	 * The vertical offset for each line, calculated as fontSize * lineHeight
	 */
	const dy = fontSize * lineHeight;
	/**
	 * Calculate the baseline offset for positioning the text correctly
	 */
	const baseY = fontUtils.getBaselineOffsetPx(
		fontFamily,
		fontSize,
		lineHeight,
	);
	/**
	 * Establish SVG anchoring instead of manual offsets.
	 * Using text-anchor avoids discrepancies between Canvas and SVG metrics
	 * for certain fonts (e.g., varying side-bearings), which caused
	 * misaligned/cropped right-aligned text previously.
	 */
	let textAnchor: 'start' | 'middle' | 'end' = 'start';
	let anchorX = 0;

	if (textAlign === 'center') {
		textAnchor = 'middle';
		anchorX = maxWidth / 2;
	} else if (textAlign === 'right') {
		textAnchor = 'end';
		anchorX = maxWidth;
	}

	/**
	 * Create the base <text> element with font and position attributes.
	 * 'xml:space="preserve"' ensures that whitespace is preserved in the text.
	 */
	const textDomElement = createDomElement(
		'text',
		{
			x: anchorX,
			y: baseY,
			'font-family': fontFamily,
			'font-size': fontSize,
			fill,
			'text-anchor': textAnchor,
			'xml:space': 'preserve',
		},
		(
			lines.length
				? []
				: undefined
		),
		'http://www.w3.org/2000/svg',
	);

	/**
	 * Process each line to create <tspan> elements with proper positioning
	 */
	for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
		/**
		 * Replace soft hyphens with regular hyphens for display
		 */
		const line = lines[lineIndex].replace(
			new RegExp(
				SOFT_HYPHEN,
				'g',
			),
			'-',
		);

		/**
		 * For empty lines, add a <tspan> with a space to ensure the line is rendered
		 */
		if (line === '') {
			textDomElement.children?.push(createDomElement(
				'tspan',
				{
					x: anchorX,
					dy: (
						lineIndex === 0
							? 0
							: dy
					),
				},
				' ',
			));
			continue;
		}

		/**
		 * Add a <tspan> for the line anchored via text-anchor and x.
		 */
		textDomElement.children?.push(createDomElement(
			'tspan',
			{
				x: anchorX,
				dy: (
					lineIndex === 0
						? 0
						: dy
				),
			},
			line,
		));
	}

	return textDomElement;
}
